import './LetsGetYouBookedMacroComponent.tsx.css';

import '../../../../js/loadSyncfusion';

import { HtmlEditor, Image, Inject, Link, QuickToolbar, RichTextEditorComponent, Toolbar } from '@syncfusion/ej2-react-richtexteditor';

import { isReservationExistingPerson, isReservationNewPerson, multiplyAndRemoveDecimal, PersonDto, ReservationPerson, ReservationState, getDisplayName } from "@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model"; // Added getDisplayName
 import { getProgramTypeDisplayName, getRoomTypeDisplayName } from "@digitalopsdev/serviceops-reservations-components/dist/EditReservation/OrderDetailsComponent";
 import { addWeeks, differenceInHours, format, isBefore, startOfDay } from 'date-fns';
 import { isReservationQuoteStatus, ReservationQuoteStatus } from '../ReservationStatus';
 import { ReactNode, useState, useEffect, useRef } from 'react'; // Added useState, useEffect, useRef
 import { countProgramDaysBetween, longToMoney, tryParseFloat } from '../../../../js/utils';
 import React from 'react';
 import { useModelState } from '../../../../js/useModelState'; // Added useModelState
 import { MacroCopyButtons } from './MacroCopyButtons';

export function LetsGetYouBookedMacroComponent({ reservationState }: LetsGetYouBookedMacroComponentProps) {
    // Ensure component re-renders if reservationState.Person changes
    useModelState(reservationState.Person);

    // State for the asynchronously fetched first name
    const [firstName, setFirstName] = useState<string>("Loading name...");
    // State for deposit calculation
    const [depositPercent, setDepositPercent] = useState("50");
    const [isDepositPercentValid, setIsDepositPercentValid] = useState(true);
    const [depositPercentParsed, setDepositPercentParsed] = useState(50);
    
    // Ref for the RichTextEditor
    const rteRef = useRef<RichTextEditorComponent>(null);

    // Effect to fetch display name and extract first name
    useEffect(() => {
        const person = reservationState.Person.value;
        if (person) {
            setFirstName("Loading name..."); // Show loading state
            getDisplayName(person, reservationState)
                .then(name => {
                    // Extract first name
                    const nameParts = (name || "").split(" ");
                    setFirstName(nameParts[0] || "Client"); // Use "Client" as fallback
                })
                .catch(error => {
                    console.error("Error fetching display name in LetsGetYouBookedMacro:", error);
                    setFirstName("Client"); // Fallback on error
                });
        } else {
            setFirstName("Client"); // Default if no person selected
        }
    }, [reservationState.Person.value]); // Dependency on person value

    // Removed internal getFirstName function

    // Removed direct assignment: let firstName = getFirstName(reservationState.Person.value);

    // Check if still loading - MUST be after all hook calls
    if (firstName === "Loading name...") {
         return <div>Loading booking details...</div>; // Or a spinner
    }
    // Removed check: if (!firstName) { return <div>Unknown person</div>; } - Handled by fallback

    // Removed: firstName = firstName.trim(); - Handled in useEffect

    // Format dates as: March 2, 2025


    let earliestDate: Date | null = null;

    const programs = reservationState.ProgramBookings.toArray();

    const transformedPrograms = {} as Record<string, {
        programName: string,
        programDays: number,
        programStartDate: string,
        programEndDate: string,
        programPrice: number,
        quantity: number
    }>;

    for (const program of programs) {

        const programName = getProgramTypeDisplayName(reservationState, program.ProgramType.value);
        const programPrice = multiplyAndRemoveDecimal(program.Price.price.value.payableAmount);
        const programDays = countProgramDaysBetween(program.Start.value, program.End.value);
        const programStartDate = format(program.Start.value, 'MMMM d, yyyy');
        const programEndDate = format(program.End.value, 'MMMM d, yyyy');

        const programStartOfDay = startOfDay(program.Start.value);

        if (!earliestDate || isBefore(programStartOfDay, earliestDate)) {
            earliestDate = programStartOfDay;
        }

        const groupKey = `${programName}_${programStartDate}_${programEndDate}`;

        const existingTransformedProgram = transformedPrograms[groupKey];

        if (!existingTransformedProgram) {
            transformedPrograms[groupKey] = {
                programName,
                programDays,
                programStartDate,
                programEndDate,
                programPrice,
                quantity: 1
            };
        } else {
            existingTransformedProgram.programPrice += programPrice;
            existingTransformedProgram.quantity += 1;
        }

    }

    const programsEle = Object.entries(transformedPrograms).map(([, program]) => {
        return (
            <React.Fragment key={program.programName}>
                <div key={program.programName}>
                    <p>
                        {program.quantity <= 1 && (
                            <>
                                <strong>Program:</strong> {program.programName}
                            </>
                        )}
                        {program.quantity > 1 && (
                            <>
                                <strong>Program:</strong> {program.quantity} x {program.programName}
                            </>
                        )}
                        <br />
                        <strong>Duration:</strong> {program.programDays} {program.programDays === 1 ? 'day' : 'days'}
                        <br />
                        <strong>Program start date: </strong> {program.programStartDate}
                        <br />
                        <strong>Program end date:</strong> {program.programEndDate}
                        <br />
                        <strong>Program Cost:</strong> ฿{longToMoney(program.programPrice)}
                    </p>
                </div>
                <hr />
            </React.Fragment>
        );
    });



    const rooms = reservationState.RoomBookings.toArray();

    const transformedRooms = {} as Record<string, {
        roomName: string,
        roomNights: number,
        roomCheckInDate: string,
        roomCheckOutDate: string,
        roomPrice: number,
        quantity: number
    }>;

    for (const room of rooms) {
        const roomName = getRoomTypeDisplayName(reservationState, room.RoomType.value);
        const roomPrice = multiplyAndRemoveDecimal(room.CalculatedPrice.value.totalPayableAmount);
        const roomNights = Math.ceil(differenceInHours(room.CheckOut.value, room.CheckIn.value) / 24) | 0;
        const roomCheckInDate = format(room.CheckIn.value, 'MMMM d, yyyy');
        const roomCheckOutDate = format(room.CheckOut.value, 'MMMM d, yyyy');
        const roomStartOfDay = startOfDay(room.CheckIn.value);

        if (!earliestDate || isBefore(roomStartOfDay, earliestDate)) {
            earliestDate = roomStartOfDay;
        }

        const groupKey = `${roomName}_${roomCheckInDate}_${roomCheckOutDate}`;
        const existingTransformedRoom = transformedRooms[groupKey];

        if (!existingTransformedRoom) {
            transformedRooms[groupKey] = {
                roomName,
                roomNights,
                roomCheckInDate,
                roomCheckOutDate,
                roomPrice,
                quantity: 1
            };
        } else {
            existingTransformedRoom.roomPrice += roomPrice;
            existingTransformedRoom.quantity += 1;
        }

    }

    const roomsEle = Object.entries(transformedRooms).map(([, room]) => {
        return (
            <React.Fragment key={room.roomName}>
                <div>
                    <p>
                        {room.quantity <= 1 && (
                            <>
                                <strong>Accommodation:</strong> {room.roomName}
                            </>
                        )}
                        {room.quantity > 1 && (
                            <>
                                <strong>Accommodation:</strong> {room.quantity} x {room.roomName}
                            </>
                        )}
                        <br />
                        <strong>Duration:</strong> {room.roomNights} {room.roomNights === 1 ? 'night' : 'nights'}
                        <br />
                        <strong>Check-in:</strong> {room.roomCheckInDate}
                        <br />
                        <strong>Check-out:</strong> {room.roomCheckOutDate}
                        <br />
                        <strong>Accommodation Cost:</strong> ฿{longToMoney(room.roomPrice)}
                    </p>
                </div>
                <hr />
            </React.Fragment>
        );
    });

    // Use standard total calculation from reservationState
    // Calculate totals using longs
    const payableLong = multiplyAndRemoveDecimal(reservationState.AmountPayableTotal.value);
    const dueLong = multiplyAndRemoveDecimal(reservationState.AmountDueTotal.value);
    const totalPaid = payableLong - dueLong; // Now subtracting longs
    const remainderDue = dueLong; // This is already a long after multiplyAndRemoveDecimal
    const totalPrice = payableLong; // Use the long value for calculations

    // Check for total price - MUST be after all hook calls
    if (totalPrice <= 0) {
        return <div>Unknown total price</div>;
    }

    const totalPriceText = longToMoney(totalPrice); // Format the full cost

    // Debouncer setup - can stay here as it's not a hook
    let depositPercentDebouncer: NodeJS.Timeout = null;

    function depositPercentChanged(e: React.ChangeEvent<HTMLInputElement>) {

        setDepositPercent(e.target.value);

        // debounce
        clearTimeout(depositPercentDebouncer);

        depositPercentDebouncer = setTimeout(() => {
            //depositPercentDebouncer = null;
            depositPercentChangedDebounced(e);
        }, 250);
    }

    function depositPercentChangedDebounced(e: React.ChangeEvent<HTMLInputElement>) {

        const value = parseFloat(e.target.value);

        if (isNaN(value) || value < 0 || value > 100) {
            setIsDepositPercentValid(false);
            return;
        }

        setIsDepositPercentValid(true);

        setDepositPercentParsed(value);
    }

    const depositRate = depositPercentParsed / 100;

    // Calculate deposit based on the full total price
    const deposit = Math.round(totalPrice * depositRate);

    const depositStr = longToMoney(deposit);

    if (!earliestDate) {
        return <div>Unknown earliest date</div>;
    }

    // 6 weeks
    let fullPaymentDueBy = startOfDay(addWeeks(earliestDate, -6));
    let lessThan6wk = false;
    if (isBefore(fullPaymentDueBy, new Date())) {
        fullPaymentDueBy = earliestDate;
        lessThan6wk = true;
    }

    const fullPaymentDueByText = format(fullPaymentDueBy, 'MMMM d, yyyy');

    return (
        <div className="flex flex-col gap-2">
            <div className="flex justify-between items-start">
                <div>
                    <label>Deposit</label>
                    <div className="flex gap-2">
                        <input className={`${(!isDepositPercentValid ? 'border-1 border-red-500' : '')} form-control w-32 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`} type="text" value={depositPercent} onChange={depositPercentChanged} /> %
                    </div>
                    {!isDepositPercentValid && (
                        <div className="text-red-500">Invalid deposit percentage</div>
                    )}
                </div>
                <MacroCopyButtons getHtml={() => rteRef.current?.getHtml() ?? null} />
            </div>
            <div>
                <RichTextEditorComponent key={depositRate} ref={rteRef}>
                    {/* Use the state variable for first name */}
                    <p>Hi {firstName},</p>
                    <br />
                    <p>
                        Perfect! Let's get you booked.
                    </p>
                    <br /><br />
                    {programsEle}
                    {roomsEle}
{/* POS Items Rendering - Updated Format */}
                    {reservationState.PosItems.value.map((item, index) => ( // Renamed posItem to item for consistency
                        <React.Fragment key={`pos-${index}`}>
                            <b>{item.Description}</b> x {item.Quantity}
                            <br />
                            <b>Price:</b> ฿{longToMoney(multiplyAndRemoveDecimal(item.Amount * item.Quantity))}
                            <hr />
                        </React.Fragment>
                    ))}
                    {/* Removed separate POS total line */}
                    <p>
                        <strong>Total Cost:</strong> ฿{totalPriceText}
                    </p>
                    <hr />
                    <p>
                        <strong><u>For Payment</u></strong>
                    </p>
                    <p>
                        We use "Wise" for payment of the booking, but do have another option if Wise isn't available in your
                        country.
                        <br /><br />
                        <a href="https://wise.com/invite/dic/liamg442"><strong>Signup to Wise Payment Processor here</strong></a>
                        <br />
                        (<a href="https://wise.com/invite/dic/liamg442">https://wise.com/signup/freetransfer</a>)
                        <br /><br />
                        Okay, this is Wise, you just have to register, not too many questions.
                    </p>
                    <p>&nbsp;</p>
                    <p>
                        Please review the below instructions and video to ensure the deposit is made correctly.
                        <br />
                        <a href="https://drive.google.com/drive/folders/10Jt8Fni2ZeHnxfrHX7PkYaT6Wdo8Rnp9?usp=drive_link">https://drive.google.com/drive/folders/10Jt8Fni2ZeHnxfrHX7PkYaT6Wdo8Rnp9?usp=drive_link</a><br /><br />Then
                        follow,
                        <br />
                        1. Click <b style={{ color: '#188038' }}>Send</b> and enter the amount.
                        <br />
                        2. On the &ldquo;Who are you sending to?&rdquo; screen, select <b style={{ color: '#188038' }}>Business</b>.
                        <br />
                        3. <b>Crucial Step:</b> Look at the top tabs and select <b style={{ color: '#188038' }}>Wise Account</b> (or <b style={{ color: '#188038' }}>Email</b>), <b style={{ color: '#c5221f' }}>not Bank Account.</b>
                        <br />
                        4. Enter our exact business email address: <b style={{ color: '#188038' }}>liam@phuketfit.co.th</b>
                        <br />
                        5. Select our business name when it pops up: <b style={{ color: '#188038' }}>Inmotion Health and Fitness Services Limited.</b>
                        <br />
                        <u>The bank fields will instantly disappear.</u>
                    </p>
                    <hr />
                    {depositPercentParsed !== 100 &&
                        <>
                            <p>
                                <strong><u>Deposit Required:</u></strong>
                            </p>
                            <p>
                                <strong>Minimum {depositPercent}% (and remainder {lessThan6wk ? 'on arrival' : '6 weeks prior to arrival'}), </strong><i>or you can choose to pay in full and save
                                    on total transfer fees and exchange rates.</i>
                            </p>
                            <p>
                                <br />
                                Option 1 - Amount {depositPercent}% = <strong>฿{depositStr} Thai Baht to be received today and the remainder to be received by {fullPaymentDueByText}.</strong>
                                <br /><br />
                                or&nbsp;
                            </p>
                            <p>
                                <br />
                                Option 2 - Amount Full Payment (to save on future fees and exchange rates) = <strong>฿{totalPriceText} Thai Baht to be received.</strong>
                            </p>
                        </>
                    }
                    {depositPercentParsed === 100 &&
                        <>
                            <p>
                                <strong><u>Payment Due</u></strong>
                            </p>
                            <p>
                                {/* Use totalPriceText for clarity when deposit is 100% */}
                                <strong>Amount Full Payment:</strong> ฿{totalPriceText} Thai Baht to be received.
                            </p>
                        </>
                    }
                    <p>
                        <br />
                        <strong>*Please select fast pay with card*</strong>
                        <br /><br />
                        <strong>**Once you have paid please send me the transfer receipt and then I will book you space, once funds arrive I will send your booking confirmation.**</strong>
                    </p>
                    <p>&nbsp;</p>
                    <p>
                        Kindly review and understand our booking terms and conditions before proceeding to book <a href="http://phuketfit.com/terms-and-conditions/">http://phuketfit.com/terms-and-conditions/</a> and our disclaimer here: <a href="http://phuketfit.com/disclaimer/">http://phuketfit.com/disclaimer/</a>
                        <br /><br />
                        Any problems please let me know.&nbsp;
                        <br />
                        &nbsp;
                    </p>
                    <p>Healthy regards,</p>
                    <p>&nbsp;</p>
                    <p>Liam&nbsp;</p>
                    <Inject services={[Toolbar, Image, Link, HtmlEditor, QuickToolbar]} />
                </RichTextEditorComponent>
            </div>
        </div>
    );
}

export interface LetsGetYouBookedMacroComponentProps {
    reservationState: ReservationState;
}
